import React, { useRef, useEffect, useState } from 'react';
import { FilterInputContainer } from '../../Commons/Facelift/Filters/styles';
import { TMinimalDescribeICustomer } from '../../../store/ducks/customers/types';
import { Select } from '@gerdau/hefesto/Select';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { uppercaseOnlyFirstLetter } from '../../../utils/formatStrings';
import BpRichDisplay from '../../BpRichDisplay';

interface CustomerSelectProps {
  title?: string;
  disabled: boolean;
  customers: TMinimalDescribeICustomer[];
  selectedCustomerId?: string;
  onSelect?(customerId: string): void;
  selectedCustomerIds?: string[];
  onSelectMultiple?(customerIds: string[]): void;
  isMultipleSelect?: boolean;
  canShowBlocks?: boolean;
  loading?: boolean;
  placeholder?: string;
  customStyles?: {
    selectTitleFontSize: string;
  };
  onBlur?: () => void;
  showRichDisplay?: boolean;
}

const buildAddress = (customer: TMinimalDescribeICustomer): string => {
  const { address, city, state, zipCode } = customer.locality;
  const cityState = city && state ? `${city} - ${state}` : city || state || '';
  return [address, cityState, zipCode ? `CEP ${zipCode}` : '']
    .filter(Boolean)
    .join(' | ');
};

const CustomerSelect: React.FC<CustomerSelectProps> = ({
  title,
  customers,
  onSelect,
  onSelectMultiple,
  selectedCustomerId,
  selectedCustomerIds,
  disabled,
  canShowBlocks = false,
  isMultipleSelect,
  placeholder,
  onBlur,
  showRichDisplay
}) => {
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  const selectedCustomer =
    showRichDisplay && selectedCustomerId
      ? customers.find((c) => c.customerId === selectedCustomerId)
      : undefined;

  useEffect(() => {
    if (!isEditing || !selectContainerRef.current) return;
    const timer = setTimeout(() => {
      const input = selectContainerRef.current?.querySelector<HTMLElement>(
        '[data-testid="hefesto-select-input"]'
      );
      input?.click();
    }, 50);
    return () => clearTimeout(timer);
  }, [isEditing]);

  const customersOptions: TSelectOptionProps[] = customers.map((customer) => ({
    label: customer.name,
    value: customer.customerId,
    disabled: canShowBlocks,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        customer.locality.address
      )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
        customer.locality.state
      } | CEP ${customer.locality.zipCode}`,
      `BP ${customer.customerId}`,
      `CNPJ ${customer.taxNumber}`
    ]
  }));

  const customersIds: string[] = customers.map(
    (customer) => customer.customerId
  );

  const onCustomerSelect = (
    customersSelected: string | string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSelectedCustomers: (customers: any) => void
  ) => {
    if (customersSelected) {
      setSelectedCustomers(customersSelected);
    }
  };

  if (showRichDisplay && !isEditing && !isMultipleSelect) {
    return (
      <BpRichDisplay
        fieldLabel={title ?? ''}
        name={selectedCustomer?.name}
        address={selectedCustomer ? buildAddress(selectedCustomer) : undefined}
        placeholder={placeholder}
        disabled={disabled}
        onClick={() => setIsEditing(true)}
      />
    );
  }

  return (
    <div ref={selectContainerRef}>
      <FilterInputContainer>
        <Select
          label={title ?? ''}
          placeholder={placeholder}
          name={'dropdownCustomers'}
          value={
            isMultipleSelect
              ? [
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  ...selectedCustomerIds!,
                  ...(isSelectedAll ||
                  selectedCustomerIds?.length === customersOptions.length
                    ? ['all']
                    : [])
                ]
              : selectedCustomerId
          }
          options={
            isMultipleSelect && customersOptions.length > 1
              ? [
                  { label: 'Selecionar todos', value: 'all' },
                  ...customersOptions
                ]
              : customersOptions
          }
          isMultipleSelect={isMultipleSelect}
          disabled={disabled}
          onBlur={() => {
            if (showRichDisplay) {
              setTimeout(() => setIsEditing(false), 150);
            }
            onBlur?.();
          }}
          onSelect={(value: string | string[]) => {
            setIsEditing(false);
            if (isMultipleSelect && onSelectMultiple) {
              const onlyCustomersSelected = (value as string[]).filter(
                (selectedCustomer) => selectedCustomer !== 'all'
              );
              const isAllSelectedCustomer =
                onlyCustomersSelected.length === customersOptions.length;

              if (
                !(value as string[]).includes('all') &&
                isAllSelectedCustomer &&
                isSelectedAll
              ) {
                setIsSelectedAll(false);
                return onCustomerSelect([], onSelectMultiple);
              }

              if (isAllSelectedCustomer) {
                setIsSelectedAll(true);
                return onCustomerSelect([...customersIds], onSelectMultiple);
              }

              if ((value as string[]).includes('all') && !isSelectedAll) {
                setIsSelectedAll(true);
                return onCustomerSelect([...customersIds], onSelectMultiple);
              }

              setIsSelectedAll(false);
              return onCustomerSelect(
                [...onlyCustomersSelected],
                onSelectMultiple
              );
            }

            if (!isMultipleSelect && onSelect) {
              onCustomerSelect(value as string[], onSelect);
            }
          }}
        ></Select>
      </FilterInputContainer>
    </div>
  );
};

export default CustomerSelect;
